/**
 * CreatorAdminPanel - Admin management UI for Creator Portal
 * 
 * Allows admins to:
 * - View all creator accounts with status badges
 * - Create new creator accounts
 * - Send/resend invites
 * - Edit creator accounts
 * - Disable/reactivate creators (soft delete)
 * - Link/unlink artifacts to creators
 * - View As another creator
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Link2, Unlink, Users, Shield, Send, Eye, Ban, RotateCcw, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useAllCreatorAccounts,
  useAllTrainingArtifacts,
  useAllTrainingProviders,
  useCreatorArtifactLinks,
  useCreateCreatorAccount,
  useUpdateCreatorAccount,
  useDisableCreatorAccount,
  useReactivateCreatorAccount,
  useSendCreatorInvite,
  useLinkArtifactToCreator,
  useUnlinkArtifactFromCreator,
  type CreatorAccount,
  type CreatorAccountStatus,
} from "@/hooks/useCreatorAdminAnalytics";

interface CreatorAdminPanelProps {
  onViewAs?: (creatorId: string, creatorName: string) => void;
}

export function CreatorAdminPanel({ onViewAs }: CreatorAdminPanelProps) {
  const { toast } = useToast();
  const { data: creators, isLoading: creatorsLoading } = useAllCreatorAccounts();
  const { data: artifacts } = useAllTrainingArtifacts();
  const { data: providers } = useAllTrainingProviders();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCreator, setEditingCreator] = useState<CreatorAccount | null>(null);
  const [linkingCreator, setLinkingCreator] = useState<CreatorAccount | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({ name: "", email: "" });
  
  const createMutation = useCreateCreatorAccount();
  const updateMutation = useUpdateCreatorAccount();
  const disableMutation = useDisableCreatorAccount();
  const reactivateMutation = useReactivateCreatorAccount();
  const inviteMutation = useSendCreatorInvite();
  const linkMutation = useLinkArtifactToCreator();
  const unlinkMutation = useUnlinkArtifactFromCreator();

  const handleCreate = async () => {
    if (!formData.name || !formData.email) {
      toast({ title: "Please fill all fields", variant: "destructive" });
      return;
    }
    
    try {
      await createMutation.mutateAsync(formData);
      toast({ 
        title: "Creator account created", 
        description: "Send an invite to allow portal access." 
      });
      setIsCreateOpen(false);
      setFormData({ name: "", email: "" });
    } catch (error: any) {
      toast({ title: "Error creating account", description: error.message, variant: "destructive" });
    }
  };

  const handleUpdate = async () => {
    if (!editingCreator || !formData.name || !formData.email) return;
    
    try {
      await updateMutation.mutateAsync({
        id: editingCreator.id,
        name: formData.name,
        email: formData.email,
        provider_id: selectedProviderId,
      });
      toast({ title: "Creator account updated" });
      setEditingCreator(null);
      setFormData({ name: "", email: "" });
      setSelectedProviderId(null);
    } catch (error: any) {
      toast({ title: "Error updating account", description: error.message, variant: "destructive" });
    }
  };

  const handleSendInvite = async (creator: CreatorAccount) => {
    const isResend = !!creator.user_id;
    try {
      const result = await inviteMutation.mutateAsync({ creatorId: creator.id, resend: isResend });
      toast({ 
        title: result.type === 'recovery' ? "Password reset sent" : "Invite sent",
        description: result.message 
      });
    } catch (error: any) {
      toast({ title: "Error sending invite", description: error.message, variant: "destructive" });
    }
  };

  const handleDisable = async (creator: CreatorAccount) => {
    try {
      await disableMutation.mutateAsync(creator.id);
      toast({ title: "Creator account disabled" });
    } catch (error: any) {
      toast({ title: "Error disabling account", description: error.message, variant: "destructive" });
    }
  };

  const handleReactivate = async (creator: CreatorAccount) => {
    try {
      await reactivateMutation.mutateAsync(creator.id);
      toast({ title: "Creator account reactivated" });
    } catch (error: any) {
      toast({ title: "Error reactivating account", description: error.message, variant: "destructive" });
    }
  };

  const openEdit = (creator: CreatorAccount) => {
    setFormData({ name: creator.name, email: creator.email });
    setSelectedProviderId(creator.provider_id);
    setEditingCreator(creator);
  };

  const getStatusBadge = (status: CreatorAccountStatus) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/20">Active</Badge>;
      case 'invited':
        return <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 border-amber-500/20">Invited</Badge>;
      case 'disabled':
        return <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">Disabled</Badge>;
      default:
        return <Badge variant="outline">No Invite</Badge>;
    }
  };

  if (creatorsLoading) {
    return <div className="text-sm text-muted-foreground">Loading creators...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Creator Accounts
            </CardTitle>
            <CardDescription>Manage creator accounts, invites, and artifact links</CardDescription>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" />
                New Creator
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Creator Account</DialogTitle>
                <DialogDescription>Add a new training content creator. You'll need to send them an invite after creating the account.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                    placeholder="Creator name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                    placeholder="creator@example.com"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {!creators || creators.length === 0 ? (
          <p className="text-sm text-muted-foreground">No creator accounts yet.</p>
        ) : (
          <div className="space-y-2">
            {creators.map((creator) => (
              <div
                key={creator.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{creator.name}</span>
                      {creator.provider_name && (
                        <Badge variant="outline" className="text-xs">
                          {creator.provider_name}
                        </Badge>
                      )}
                      {creator.is_admin && (
                        <Badge variant="secondary" className="text-xs">
                          <Shield className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                      {getStatusBadge(creator.status)}
                    </div>
                    <div className="text-xs text-muted-foreground">{creator.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{creator.artifact_count || 0} artifacts</Badge>
                  
                  {/* Send/Resend Invite button */}
                  {creator.status !== 'active' && creator.status !== 'disabled' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleSendInvite(creator)}
                      disabled={inviteMutation.isPending}
                      title={creator.user_id ? "Resend Invite" : "Send Invite"}
                    >
                      {inviteMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  )}

                  {/* View As button (only for active creators) */}
                  {creator.status === 'active' && !creator.is_admin && onViewAs && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewAs(creator.id, creator.name)}
                      title="View As"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setLinkingCreator(creator)}
                    title="Manage artifacts"
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEdit(creator)}
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  
                  {/* Disable/Reactivate actions (not for admins) */}
                  {!creator.is_admin && (
                    <>
                      {creator.status === 'disabled' ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" title="Reactivate">
                              <RotateCcw className="h-4 w-4 text-green-600" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Reactivate Creator Account?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will restore {creator.name}'s access to the Creator Portal.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleReactivate(creator)}>
                                Reactivate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" title="Disable">
                              <Ban className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Disable Creator Account?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will prevent {creator.name} from accessing the Creator Portal. 
                                Their data and artifact links will be preserved.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDisable(creator)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Disable
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingCreator} onOpenChange={(open) => !open && setEditingCreator(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Creator Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="edit-provider">Provider (auto-links artifacts)</Label>
                <Select
                  value={selectedProviderId || ""}
                  onValueChange={(val) => setSelectedProviderId(val || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a provider..." />
                  </SelectTrigger>
                  <SelectContent>
                    {providers?.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Changing the provider will re-link to the new provider's artifacts.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingCreator(null)}>Cancel</Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Artifacts Dialog */}
        <Dialog open={!!linkingCreator} onOpenChange={(open) => !open && setLinkingCreator(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Manage Artifacts for {linkingCreator?.name}</DialogTitle>
              <DialogDescription>Link or unlink training artifacts</DialogDescription>
            </DialogHeader>
            {linkingCreator && (
              <ArtifactLinker
                creator={linkingCreator}
                artifacts={artifacts || []}
                onLink={(artifactId) => {
                  linkMutation.mutate({ creatorId: linkingCreator.id, artifactId });
                }}
                onUnlink={(linkId) => {
                  unlinkMutation.mutate({ linkId, creatorId: linkingCreator.id });
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

interface ArtifactLinkerProps {
  creator: CreatorAccount;
  artifacts: { id: string; title: string; provider_name: string }[];
  onLink: (artifactId: string) => void;
  onUnlink: (linkId: string) => void;
}

function ArtifactLinker({ creator, artifacts, onLink, onUnlink }: ArtifactLinkerProps) {
  const { data: links, isLoading } = useCreatorArtifactLinks(creator.id);
  const [selectedArtifact, setSelectedArtifact] = useState<string>("");

  const linkedArtifactIds = new Set((links || []).map(l => l.artifact_id));
  const availableArtifacts = artifacts.filter(a => !linkedArtifactIds.has(a.id));

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Currently linked */}
      <div>
        <Label className="text-sm font-medium">Linked Artifacts</Label>
        {!links || links.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-1">No artifacts linked yet.</p>
        ) : (
          <div className="mt-2 space-y-1">
            {links.map((link: any) => (
              <div key={link.id} className="flex items-center justify-between p-2 rounded border">
                <span className="text-sm">{link.training_artifacts?.title || 'Unknown'}</span>
                <Button variant="ghost" size="sm" onClick={() => onUnlink(link.id)}>
                  <Unlink className="h-3 w-3 mr-1" />
                  Unlink
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add new link */}
      <div>
        <Label className="text-sm font-medium">Link New Artifact</Label>
        <div className="flex gap-2 mt-2">
          <Select value={selectedArtifact} onValueChange={setSelectedArtifact}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select an artifact" />
            </SelectTrigger>
            <SelectContent>
              {availableArtifacts.map((artifact) => (
                <SelectItem key={artifact.id} value={artifact.id}>
                  {artifact.title} ({artifact.provider_name})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => {
              if (selectedArtifact) {
                onLink(selectedArtifact);
                setSelectedArtifact("");
              }
            }}
            disabled={!selectedArtifact}
          >
            <Link2 className="h-4 w-4 mr-1" />
            Link
          </Button>
        </div>
      </div>
    </div>
  );
}
